import React from "react";

import {
  BookmarkAddRounded,
  BookmarkRemoveRounded,
  CircleRounded,
  ExpandMoreRounded,
} from "@mui/icons-material";
import { Box, ButtonBase, Collapse, IconButton, SvgIconTypeMap } from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";

import useModal from "../../../../../config/context/modalContext";
import { GreyPallet } from "../../../../../config/styles";
import { UtilComponents } from "../../../../../config/util/utilComponents";

import ExpandMore from "../../../../../components/ExpandMore";
import MainText from "../../../../../components/MainText";

type DataItemProps = {
  data: any;
  tagAction?: () => void;
  isTagged?: boolean;
  tagIcon?: OverridableComponent<SvgIconTypeMap<{}, "svg">> & { muiName: string };
};

export default function DataItem({ data, tagAction, isTagged, tagIcon }: DataItemProps) {
  const { pushAlert } = useModal();

  const isProfessor = "id_docente" in data;

  const [showDetail, setShowDetail] = React.useState(false);

  const IconComponent = tagIcon || CircleRounded;

  return (
    <Box
      style={{ width: "100%" }}
      mb={2}
      sx={{
        boxSizing: "border-box",
        backgroundColor: GreyPallet.Light,
        borderRadius: "15px",
        "& *": {
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        display={"flex"}
        justifyContent={"start"}
        alignItems={"center"}
        style={{ backgroundColor: GreyPallet.Light, borderRadius: 15, width: "100%" }}
        px={2}
        py={1}
      >
        <ButtonBase
          sx={{ mr: 1, borderRadius: "50%" }}
          onClick={() =>
            pushAlert({
              children: (
                <Box textAlign={"center"}>
                  Nota: {parseFloat(data.media_atuacao_profissional_media).toFixed(2)}
                  <br />
                  Desvio: {parseFloat(data.media_atuacao_profissional_DP).toFixed(2)}
                  <br />
                  Aprovação: {parseInt(data.media_aprovacao)}%
                </Box>
              ),
              type: "info",
            })
          }
        >
          <IconComponent
            sx={{
              color: UtilComponents.getRatingColor(
                data.media_atuacao_profissional_media,
                data.media_atuacao_profissional_DP
              ),
            }}
            fontSize={"small"}
          />
        </ButtonBase>

        {isProfessor ? (
          <Box>
            <MainText size="caption">{data.nome_docente}</MainText>
            <MainText size="tiny" color="regular">
              Nota: {parseFloat(data.media_atuacao_profissional_media).toFixed(2)}
              {" - "}
              Desvio: {parseFloat(data.media_atuacao_profissional_DP).toFixed(2)}
              {" - "}
              Aprovação: {parseInt(data.media_aprovacao)}%
            </MainText>
          </Box>
        ) : (
          <Box>
            <MainText size="tiny" color="regular">
              Nota: {parseFloat(data.media_atuacao_profissional_media).toFixed(2)}
              {" - "}
              Desvio: {parseFloat(data.media_atuacao_profissional_DP).toFixed(2)}
              {" - "}
              Aprovação: {parseInt(data.media_aprovacao)}%
            </MainText>
            <MainText size="caption">
              {data.codigo} - {data.nome}
            </MainText>
          </Box>
        )}

        <ExpandMore
          expand={showDetail}
          onClick={() => setShowDetail(!showDetail)}
          aria-expanded={showDetail}
        >
          <ExpandMoreRounded />
        </ExpandMore>
      </Box>

      <Collapse in={showDetail} timeout="auto" unmountOnExit sx={{}}>
        <Box sx={{ width: "100%", pt: 1, pb: isProfessor && !!tagAction ? 0 : 1, pl: 3, pr: 2 }}>
          {!isProfessor && (
            <Box mb={1}>
              <MainText size="tiny" color="regular">
                {data.unidade_responsavel}
              </MainText>
            </Box>
          )}
          {data[isProfessor ? "lista_componentes" : "lista_docentes"].map(
            (item: any, i: number) => (
              <Box
                key={isProfessor ? `${item.codigo}-${i}` : `${item.id_docente}-${i}`}
                display={"flex"}
                justifyContent={"start"}
                alignItems={"center"}
                mb={1}
              >
                <ButtonBase
                  sx={{ mr: 1, borderRadius: "50%" }}
                  onClick={() =>
                    pushAlert({
                      children: (
                        <Box textAlign={"center"}>
                          Nota: {parseFloat(item.atuacao_profissional_media).toFixed(2)}
                          <br />
                          Desvio: {parseFloat(item.atuacao_profissional_DP).toFixed(2)}
                          {isProfessor ? (
                            <>
                              <br />
                              Ano: {item.ano_x}.{item.periodo_x}
                              <br />
                              Aprovação: {parseInt(item.percentage)}%
                            </>
                          ) : (
                            <>
                              <br />
                              Aprovação: {parseInt(item.media_aprovacao)}%
                            </>
                          )}
                        </Box>
                      ),
                      type: "info",
                    })
                  }
                >
                  <CircleRounded
                    sx={{
                      color: UtilComponents.getRatingColor(
                        item.atuacao_profissional_media,
                        item.atuacao_profissional_DP
                      ),
                      mr: 1,
                      fontSize: 16,
                    }}
                  />
                </ButtonBase>
                <Box display={"flex"} justifyContent={"start"} alignItems={"center"} gap={"5px"}>
                  <MainText size="small">
                    {isProfessor ? `${item.codigo} - ${item.nome}` : item.nome_docente}
                  </MainText>
                  <MainText size="tiny" color="ghost-regular">
                    {isProfessor
                      ? parseInt(item.percentage) + "%"
                      : parseInt(item.media_aprovacao) + "%"}
                  </MainText>
                </Box>
              </Box>
            )
          )}
        </Box>
        {isProfessor && !!tagAction && (
          <Box display={"flex"} justifyContent={"end"} px={2} pb={1}>
            <IconButton onClick={tagAction}>
              {isTagged ? <BookmarkRemoveRounded /> : <BookmarkAddRounded />}
            </IconButton>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}
